/**
 * Stocks (in place of TornTools' total portfolio value, cost to next stock and
 * stock acronyms): your holdings valued at today's price, what you paid, and for
 * each benefit how many blocks you have and what the next block costs. Pure; from
 * v1 /torn/?selections=stocks and /user/?selections=stocks.
 *
 * Since v0.123.0 (website roadmap S13) also the website's Stocks page: price history kept by the server (thinned as it
 * ages), each stock's 30-day range, changes and sparkline, simple rules of thumb (never advice), and the investment ROI
 * table (every benefit block, the bank's terms, an optional private island rental). Used by the extension, the website
 * and the server alike.
 */

import { BANK_MAX, bankEarnings } from './torn-sims';

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x) ? Number(x) : null);
const str = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x.trim() : null);
const entries = (x: unknown): Obj[] => (Array.isArray(x) ? x : Object.values(obj(x) ?? {})).map(obj).filter((o): o is Obj => !!o);

export interface Stock {
  id: number;
  name: string;
  acronym: string;
  price: number;
  /** Shares for one benefit block. */
  requirement: number | null;
  benefit: string | null;
  /** "active" (paid out every N days) or "passive". */
  kind: string | null;
  frequency: number | null;
  /** Torn's market cap, shares in issue and investors (the website's market table; null when Torn doesn't say). */
  marketCap?: number | null;
  totalShares?: number | null;
  investors?: number | null;
}

export function readStocks(body: unknown): Stock[] {
  return entries(obj(body)?.stocks)
    .map((s): Stock | null => {
      const id = num(s.stock_id) ?? num(s.id);
      const price = num(s.current_price) ?? num(obj(s.market)?.price);
      if (!id || price === null) return null;
      const b = obj(s.benefit) ?? obj(s.bonus);
      return {
        id,
        name: str(s.name) ?? `Stock ${id}`,
        acronym: str(s.acronym) ?? '',
        price,
        requirement: num(b?.requirement),
        benefit: str(b?.description),
        kind: str(b?.type) ?? (b?.passive === true ? 'passive' : b ? 'active' : null),
        frequency: num(b?.frequency),
        marketCap: num(s.market_cap) ?? num(obj(s.market)?.cap),
        totalShares: num(s.total_shares) ?? num(obj(s.market)?.shares),
        investors: num(s.investors) ?? num(obj(s.market)?.investors),
      };
    })
    .filter((s): s is Stock => !!s)
    .sort((a, b) => a.acronym.localeCompare(b.acronym));
}

export interface Holding {
  id: number;
  shares: number;
  /** What you paid, from the transactions Torn lists (null when it lists none). */
  paid: number | null;
  /** Your dividend: ready to collect, and the days built up of its period (when Torn says). */
  ready?: boolean;
  progress?: number | null;
}

export function readHoldings(body: unknown): Holding[] {
  return entries(obj(body)?.stocks)
    .map((s): Holding | null => {
      const id = num(s.stock_id) ?? num(s.id);
      const shares = num(s.total_shares) ?? num(s.shares);
      if (!id || !shares) return null;
      const tx = entries(s.transactions);
      const paid = tx.length ? tx.reduce((t, x) => t + (num(x.shares) ?? 0) * (num(x.bought_price) ?? num(x.price) ?? 0), 0) : null;
      const b = obj(s.benefit) ?? obj(s.dividend) ?? obj(s.bonus);
      return { id, shares, paid, ...(b ? { ready: num(b.ready) === 1 || b.ready === true, progress: num(b.progress) } : {}) };
    })
    .filter((h): h is Holding => !!h);
}

export interface Line {
  stock: Stock;
  shares: number;
  value: number;
  paid: number | null;
  gain: number | null;
  /** Benefit blocks held (active stocks can stack; passive: one is enough). */
  blocks: number;
  /** Shares and money to the next block (null: no requirement known, or passive and held). */
  nextShares: number | null;
  nextCost: number | null;
}

export function portfolio(stocks: readonly Stock[], holdings: readonly Holding[]): { lines: Line[]; value: number; paid: number | null; gain: number | null } {
  const lines: Line[] = [];
  for (const s of stocks) {
    const h = holdings.find((x) => x.id === s.id);
    const shares = h?.shares ?? 0;
    const req = s.requirement;
    // Active benefits double the shares needed for each further block (1×, 2×, 4×…).
    let blocks = 0;
    let need = req ?? 0;
    if (req) {
      if (s.kind === 'passive') blocks = shares >= req ? 1 : 0;
      else {
        let have = shares;
        let step = req;
        while (have >= step) {
          blocks++;
          have -= step;
          step *= 2;
        }
        need = step - have;
      }
    }
    const nextShares = !req ? null : s.kind === 'passive' ? (blocks ? null : req - shares) : need;
    lines.push({ stock: s, shares, value: shares * s.price, paid: h?.paid ?? null, gain: h?.paid ? shares * s.price - h.paid : null, blocks, nextShares, nextCost: nextShares === null ? null : Math.round(nextShares * s.price) });
  }
  const held = lines.filter((l) => l.shares > 0);
  const value = held.reduce((t, l) => t + l.value, 0);
  const paidAll = held.every((l) => l.paid !== null) && held.length ? held.reduce((t, l) => t + (l.paid ?? 0), 0) : null;
  return { lines, value, paid: paidAll, gain: paidAll === null ? null : value - paidAll };
}

/**
 * What a benefit pays out, in money: "$50,000,000" → 50m; "2x Drug Packs" → 2 × that item's value; anything else
 * (energy, discounts, "Advanced firewall"…) null. `itemValue` gives an item's market value by its name.
 */
export function benefitValue(benefit: string | null, itemValue: (name: string) => number | null): number | null {
  if (!benefit) return null;
  const money = /^\$([\d,]+)$/.exec(benefit.trim());
  if (money) return Number(money[1]!.replace(/,/g, ''));
  const item = /^(\d+)x\s+(.+)$/i.exec(benefit.trim());
  if (item) {
    const v = itemValue(item[2]!.trim());
    return v === null ? null : Number(item[1]) * v;
  }
  return null;
}

export interface StockRoi {
  id: number;
  /** A year of this stock's dividend, in money (null: not worked out). */
  yearly: number | null;
  /** What the next block returns in a year for what it costs, in % (null: no money value or nothing to buy). */
  roi: number | null;
}

/**
 * Return on the next block (owner's suggestion 214433-1ac4, like FF Scouter's investment guide): a year of the
 * dividend (paid every `frequency` days) against what the next block costs. Passive benefits and ones without a
 * money value get none. Sorted best first; the first is the one to buy next.
 */
export function stockRois(lines: readonly Line[], itemValue: (name: string) => number | null): StockRoi[] {
  return lines
    .map((l): StockRoi => {
      const v = l.stock.kind === 'passive' || !l.stock.frequency ? null : benefitValue(l.stock.benefit, itemValue);
      const yearly = v === null ? null : (v * 365) / l.stock.frequency!;
      const roi = yearly === null || !l.nextCost ? null : Math.round((yearly / l.nextCost) * 1000) / 10;
      return { id: l.stock.id, yearly, roi };
    })
    .sort((a, b) => (b.roi ?? -1) - (a.roi ?? -1));
}

// ---------------------------------------------------------------- price history (the server keeps it; website S13)

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** One price seen (ms, dollars). */
export interface StockPoint {
  at: number;
  price: number;
}

/** How the server thins prices: every reading for a week, one an hour to 31 days, then one a day (no end date — `dailyMs` was the old limit). */
export const STOCK_KEEP = { fullMs: 7 * DAY, hourlyMs: 31 * DAY, dailyMs: 400 * DAY } as const;
/** The server's readings: every 5–60 minutes (STOCK_HISTORY_MINUTES; 15 by default). */
export const STOCK_EVERY_MIN = 5;
export const STOCK_EVERY_MAX = 60;
export const STOCK_EVERY_DEFAULT = 15;
export const clampStockEvery = (n: unknown): number => {
  const v = Math.round(Number(n));
  return Number.isFinite(v) ? Math.max(STOCK_EVERY_MIN, Math.min(STOCK_EVERY_MAX, v)) : STOCK_EVERY_DEFAULT;
};

/**
 * Thins a stock's history (time order) as it ages: every point of the last week, then the last point of each hour up to
 * 31 days, then the last point of each day — with no end date (terms 19, "No age limits"; before, days past 400 went).
 * Returns a new list in time order.
 */
export function thinStockPoints(points: readonly StockPoint[], now: number): StockPoint[] {
  const out: StockPoint[] = [];
  let bucket: string | null = null;
  for (const p of [...points].sort((a, b) => a.at - b.at)) {
    const age = now - p.at;
    const b = age <= STOCK_KEEP.fullMs ? `p${p.at}` : age <= STOCK_KEEP.hourlyMs ? `h${Math.floor(p.at / HOUR)}` : `d${Math.floor(p.at / DAY)}`;
    if (b === bucket) out[out.length - 1] = p;
    else out.push(p);
    bucket = b;
  }
  return out;
}

/** The price at a time: the last point at or before it, if the history reaches back that far (null otherwise). */
export function priceAt(points: readonly StockPoint[], at: number, slackMs = HOUR): number | null {
  if (!points.length || points[0]!.at > at + slackMs) return null;
  let best: StockPoint | null = null;
  for (const p of points) {
    if (p.at <= at) best = p;
    else break;
  }
  return (best ?? points[0]!).price;
}

const avg = (xs: readonly number[]): number | null => (xs.length ? xs.reduce((t, x) => t + x, 0) / xs.length : null);
const pct = (now: number, then: number | null): number | null => (then && then > 0 ? Math.round(((now - then) / then) * 10_000) / 100 : null);

export interface StockStats {
  /** The oldest price kept (ms; null: none yet). */
  since: number | null;
  /** Lowest and highest price in the last 30 days (or since the history starts). */
  low30: number | null;
  high30: number | null;
  /** Price change in %, over a day, a week, 30 days and a year — null when the history doesn't reach back that far. */
  change: { d1: number | null; d7: number | null; d30: number | null; d365: number | null };
  /** Average price over the last day (the rules of thumb compare today's price with it) and the last 7 days. */
  avg1: number | null;
  avg7: number | null;
  /** Up to 60 prices over the last 30 days, evenly spaced in time (a sparkline). */
  spark: number[];
  /** The oldest price kept within the last 365 days (a block's return over the history: `blockReturn`). */
  yearStart: StockPoint | null;
}

/** A stock's figures from its history (time order) and today's price. Pure. */
export function stockStats(points: readonly StockPoint[], price: number, now: number): StockStats {
  const list = [...points].sort((a, b) => a.at - b.at);
  const month = list.filter((p) => p.at >= now - 30 * DAY);
  const prices = [...month.map((p) => p.price), price];
  // A day's change needs the history to reach back about a day; longer periods allow a little more slack.
  const change = (days: number) => pct(price, priceAt(list, now - days * DAY, Math.max(HOUR, days * DAY * 0.05)));
  const spark: number[] = [];
  if (month.length) {
    const from = month[0]!.at;
    const step = Math.max(1, (now - from) / 59);
    for (let i = 0; i < 60; i++) {
      const t = from + i * step;
      if (t > now) break;
      spark.push(priceAt(month, t, step) ?? month[0]!.price);
    }
    spark[spark.length - 1] = price;
  }
  return {
    since: list[0]?.at ?? null,
    low30: list.length ? Math.min(...prices) : null,
    high30: list.length ? Math.max(...prices) : null,
    change: { d1: change(1), d7: change(7), d30: change(30), d365: change(365) },
    avg1: avg(list.filter((p) => p.at >= now - DAY).map((p) => p.price)),
    avg7: avg(list.filter((p) => p.at >= now - 7 * DAY).map((p) => p.price)),
    spark,
    yearStart: list.find((p) => p.at >= now - 365 * DAY) ?? null,
  };
}

/**
 * What holding ONE benefit block would have returned over the history kept (at most `days`): the price change plus the
 * benefit's money paid in that time (value × days ÷ period), against what the block cost then. `days` = the days the
 * history covers (null: under a week of history, or no block to hold). The price-only change is there too.
 */
export function blockReturn(stock: Stock, points: readonly StockPoint[], now: number, itemValue: (name: string) => number | null, maxDays = 365): { days: number | null; price: number | null; withBenefit: number | null } {
  const list = [...points].sort((a, b) => a.at - b.at);
  const first = list.find((p) => p.at >= now - maxDays * DAY) ?? null;
  if (!first || now - first.at < 7 * DAY - HOUR) return { days: null, price: null, withBenefit: null };
  const days = Math.round((now - first.at) / DAY);
  const price = pct(stock.price, first.price);
  const value = stock.kind === 'passive' || !stock.frequency ? null : benefitValue(stock.benefit, itemValue);
  const req = stock.requirement;
  const withBenefit = value === null || !req ? null : Math.round((((stock.price - first.price) * req + (value * days) / stock.frequency!) / (first.price * req)) * 10_000) / 100;
  return { days, price, withBenefit };
}

// ---------------------------------------------------------------- rules of thumb (never advice)

export type SignalKind = 'buy' | 'sell' | 'hold' | 'unknown';

export interface StockSignal {
  kind: SignalKind;
  /** Where today's price sits in its 30-day range, 0 (the low) … 100 (the high); null: no range yet. */
  place: number | null;
  /** 'up' / 'down' / 'flat': today's price against the last 24 hours' average. */
  trend: 'up' | 'down' | 'flat' | null;
  /** Plain words: why. */
  why: string;
}

/** Days of history before any rule of thumb is shown. */
export const SIGNAL_MIN_DAYS = 3;

/**
 * A rule of thumb from past prices only — "cheap near its monthly low and turning up, dear near its high and turning
 * down". It knows nothing of Torn's news, the benefit's worth to you, or what comes next: the page says so beside it.
 */
export function stockSignal(stats: StockStats, price: number, now: number): StockSignal {
  if (stats.since === null || now - stats.since < SIGNAL_MIN_DAYS * DAY || stats.low30 === null || stats.high30 === null) {
    return { kind: 'unknown', place: null, trend: null, why: `Not enough price history yet (needs ${SIGNAL_MIN_DAYS} days).` };
  }
  const span = stats.high30 - stats.low30;
  const place = span > 0 ? Math.round(((price - stats.low30) / span) * 100) : 50;
  const trend = stats.avg1 === null ? null : price > stats.avg1 * 1.005 ? 'up' : price < stats.avg1 * 0.995 ? 'down' : 'flat';
  // A range under 2% of the price is noise: nothing to say.
  if (span < price * 0.02) return { kind: 'hold', place, trend, why: 'Barely moved this month.' };
  if (place <= 20) {
    return trend === 'down' ? { kind: 'hold', place, trend, why: 'Near its 30-day low but still falling — wait for it to turn.' } : { kind: 'buy', place, trend, why: `Near its 30-day low${trend === 'up' ? ' and turning up' : ''}.` };
  }
  if (place >= 80) {
    return trend === 'up' ? { kind: 'hold', place, trend, why: 'Near its 30-day high and still rising.' } : { kind: 'sell', place, trend, why: `Near its 30-day high${trend === 'down' ? ' and turning down' : ''}.` };
  }
  return { kind: 'hold', place, trend, why: `Mid-range this month${trend === 'up' ? ', rising' : trend === 'down' ? ', falling' : ''}.` };
}

// ---------------------------------------------------------------- investment ROI (FF Scouter's calculator)

export interface InvestRow {
  key: string;
  kind: 'block' | 'bank' | 'property';
  /** "TSB block 2", "Bank — 3 months", "Private island rental". */
  label: string;
  stockId: number | null;
  /** Benefit block number (1 = the first; each next one needs twice the shares). */
  block: number | null;
  /** Shares this block needs (blocks only). */
  shares: number | null;
  /** Money put in. */
  cost: number;
  /** Money it pays a year. */
  yearly: number;
  /** Yearly return, % of the cost. */
  roi: number;
  /** Days until it has paid for itself (null: the bank — your money comes back at the end of the term). */
  payback: number | null;
  /** You hold this block already. */
  owned: boolean;
  /** The next block of this stock you'd buy. */
  next: boolean;
  /** Bank: the term in days. */
  days: number | null;
}

export interface InvestInput {
  stocks: readonly Stock[];
  holdings: readonly Holding[];
  itemValue: (name: string) => number | null;
  /** Torn's bank terms (yearly % as its bank page shows them). */
  bank?: ReadonlyArray<{ days: number; label: string; rate: number }>;
  /** Bank Interest merits (0–10) and the Torn City Investments perk; the amount (at most $2b; default $2b). */
  bankOpts?: { merits?: number; tci?: boolean; amount?: number };
  /** A private island rented out: what it cost (with upgrades), the rent a day and its upkeep a day. */
  property?: { cost: number; rentPerDay: number; upkeepPerDay?: number } | null;
  /** Blocks listed per stock (default 10: the 10th needs 512× the first's shares). */
  maxBlocks?: number;
}

/**
 * Every investment the calculator compares, best yearly return first: each benefit block of every stock whose benefit
 * has a money value (money as it says, items at market value), the bank's terms, and an optional private island
 * rental. `noValue`: stocks whose benefit has no money value (passive perks, discounts, energy…) — listed, not ranked.
 */
export function investmentRows(input: InvestInput): { rows: InvestRow[]; noValue: Stock[] } {
  const rows: InvestRow[] = [];
  const noValue: Stock[] = [];
  const lines = portfolio(input.stocks, input.holdings).lines;
  const maxBlocks = Math.max(1, Math.min(20, input.maxBlocks ?? 10));
  for (const l of lines) {
    const s = l.stock;
    const value = s.kind === 'passive' || !s.frequency ? null : benefitValue(s.benefit, input.itemValue);
    if (value === null || value <= 0 || !s.requirement || !s.frequency || s.price <= 0) {
      if (s.benefit) noValue.push(s);
      continue;
    }
    const yearly = (value * 365) / s.frequency;
    for (let n = 1; n <= maxBlocks; n++) {
      const shares = s.requirement * 2 ** (n - 1);
      const cost = Math.round(shares * s.price);
      rows.push({ key: `b${s.id}-${n}`, kind: 'block', label: `${s.acronym} block ${n}`, stockId: s.id, block: n, shares, cost, yearly, roi: Math.round((yearly / cost) * 10_000) / 100, payback: Math.ceil((cost / value) * s.frequency), owned: n <= l.blocks, next: n === l.blocks + 1, days: null });
    }
  }
  for (const b of input.bank ?? []) {
    const amount = Math.max(0, Math.min(BANK_MAX, input.bankOpts?.amount ?? BANK_MAX));
    if (!amount || !(b.rate > 0) || !(b.days > 0)) continue;
    const e = bankEarnings(amount, b.rate, b.days, input.bankOpts ?? {});
    rows.push({ key: `bank-${b.days}`, kind: 'bank', label: `Bank — ${b.label}`, stockId: null, block: null, shares: null, cost: amount, yearly: Math.round((amount * e.rate) / 100), roi: Math.round(e.rate * 100) / 100, payback: null, owned: false, next: false, days: b.days });
  }
  const pi = input.property;
  if (pi && pi.cost > 0 && pi.rentPerDay > 0) {
    const perDay = pi.rentPerDay - Math.max(0, pi.upkeepPerDay ?? 0);
    if (perDay > 0) rows.push({ key: 'pi', kind: 'property', label: 'Private island rental', stockId: null, block: null, shares: null, cost: Math.round(pi.cost), yearly: Math.round(perDay * 365), roi: Math.round(((perDay * 365) / pi.cost) * 10_000) / 100, payback: Math.ceil(pi.cost / perDay), owned: false, next: false, days: null });
  }
  rows.sort((a, b) => b.roi - a.roi || a.cost - b.cost);
  return { rows, noValue };
}

// ---------------------------------------------------------------- what the server answers

/** One stock in `GET /api/stocks`: Torn's figures, the history's figures and a sparkline. */
export interface StockMarketRow extends Stock {
  stats: StockStats;
}

/** `GET /api/stocks` (website Stocks page): the server's last reading of Torn's stocks (public data, no players). */
export interface StocksAnswer {
  /** When Torn was last read (ms; null: never yet). */
  at: number | null;
  /** Minutes between readings. */
  every: number;
  /** The server reads Torn's stocks (it has its own key and the reading is on). */
  polling: boolean;
  stocks: StockMarketRow[];
}

/** `GET /api/stocks/:id/history?days=`: one stock's kept prices over the last 1–365 days. */
export interface StockHistoryAnswer {
  id: number;
  days: number;
  points: StockPoint[];
}
